import { routes, DOCS_SECTIONS } from 'constants/index';
import { ProductKey } from 'constants/products';

export interface NavLinkItem {
  key: string;
  label: string;
  to: string;
}

export interface NavItem {
  key: string;
  label: string;
  // Absent when the item only opens a submenu.
  to?: string;
  // primeicons class, used by the sidebar's collapsed rail where the label is
  // hidden. The horizontal bar ignores it.
  icon: string;
  submenus: NavLinkItem[];
  // Heading the sidebar groups the item under. Consecutive items sharing one
  // render under a single heading. Empty means no heading at all.
  section?: string;
}

// Falls back to a neutral glyph so a menu item added server-side still renders
// in the rail rather than leaving a blank row.
const ICONS: Record<string, string> = {
  dashboard: 'pi-home',
  list0: 'pi-database',
  list1: 'pi-file-edit',
  list2: 'pi-clock',
  list3: 'pi-user',
  list4: 'pi-check-circle',
  list5: 'pi-file',
  list6: 'pi-list',
  logs: 'pi-history',
  'cargo-claims': 'pi-box',
  'health-dashboard': 'pi-th-large',
  'closed-tickets': 'pi-chart-bar',
  'reimbursement-approval': 'pi-check-square',
  'reimbursement-claims': 'pi-wallet',
  'direct-billing': 'pi-file',
};

function iconFor(key: string): string {
  return ICONS[key] || 'pi-circle';
}

/*
 * What each endorsement section is called in the sidebar. The server still
 * names them by number (List 1, List 2...), which says where a ticket sits in
 * the pipeline but not what the page is for. An identifier missing here keeps
 * the server's name.
 */
const LABELS: Record<string, string> = {
  dashboard: 'Overview',
  list0: 'Source Data',
  list1: 'Pending Requests',
  list2: 'In Progress',
  list3: 'Insured Principals',
  list4: 'Closed Endorsements',
  list5: 'Invoices',
  list6: 'Invoice Line Items',
  logs: 'Activity Logs',
};

function labelFor(identifier: string, fallback: string): string {
  return LABELS[identifier] ?? fallback;
}

/*
 * The sidebar headings for endorsement, in the order they appear. Overview
 * leads on its own, with no heading above it. A menu item the server adds
 * without a section here lands at the end under the last heading rather than
 * disappearing.
 */
const ENDORSEMENT_SECTIONS: { label: string; keys: string[] }[] = [
  { label: '', keys: ['dashboard'] },
  { label: 'Endorsements', keys: ['list1', 'list2', 'list3', 'list4'] },
  { label: 'Billing', keys: ['list5', 'list6'] },
  { label: 'System', keys: ['list0', 'logs'] },
];

function groupBySection(items: NavItem[]): NavItem[] {
  const fallback = ENDORSEMENT_SECTIONS[ENDORSEMENT_SECTIONS.length - 1].label;
  const sectionOf = (key: string) =>
    ENDORSEMENT_SECTIONS.findIndex((section) => section.keys.includes(key));
  const rank = (key: string) => {
    const index = sectionOf(key);
    return index === -1 ? ENDORSEMENT_SECTIONS.length : index;
  };

  return items
    .map((item, order) => ({ item, order }))
    .sort((a, b) => rank(a.item.key) - rank(b.item.key) || a.order - b.order)
    .map(({ item }) => ({
      ...item,
      section: ENDORSEMENT_SECTIONS[sectionOf(item.key)]?.label ?? fallback,
    }));
}

type ServerMenu = {
  identifier: string;
  name: string;
  submenus: { identifier: string; name: string }[];
}[];

const LIST_DEFAULTS = 'page=1&rows=50&first=0';

/*
 * Maps a server menu identifier to its route. Previously inline in the header's
 * JSX, which meant the nav was assembled in three places: hardcoded links,
 * this switch, and the product links added for cargo claims.
 */
function routeFor(identifier: string): string {
  switch (identifier) {
    case 'list2':
      return `${routes.listTwo.index}?${LIST_DEFAULTS}&ticketStatus=All&ticketType=All`;
    case 'list3':
      return `${routes.listThree.index}?${LIST_DEFAULTS}`;
    case 'list4':
      return `${routes.listFour.index}?${LIST_DEFAULTS}`;
    case 'list5':
      return `${routes.listFive.index}?${LIST_DEFAULTS}`;
    case 'list6':
      return `${routes.listSix.index}?${LIST_DEFAULTS}`;
    case 'logs':
      return `${routes.logs.index}?list=1&${LIST_DEFAULTS}`;
    default:
      return routes.main;
  }
}

function submenuRouteFor(identifier: string): string {
  if (identifier === 'list1-summary') {
    return `${routes.listOne.index}/${routes.listOne.statuses}`;
  }

  return `${routes.listOne.index}?${LIST_DEFAULTS}&ticketStatus=All&ticketType=All`;
}

/*
 * The single place the nav is assembled.
 *
 * Dashboard and List 0 are still declared here rather than coming from the
 * server menu, matching what the API returns today. Once menu items carry a
 * `product` field they should come from the same source as the rest, and this
 * function becomes a pure mapping.
 */
export function buildNavItems(
  product: ProductKey,
  serverMenu: ServerMenu = []
): NavItem[] {
  if (product === 'cargoClaims') {
    return [
      {
        key: 'cargo-claims',
        label: 'Claims',
        to: `${routes.cargoClaims.index}?${LIST_DEFAULTS}&status=All`,
        icon: iconFor('cargo-claims'),
        submenus: [],
        section: 'Cargo claims',
      },
    ];
  }

  if (product === 'healthClaims') {
    const tab = (
      key: string,
      label: string,
      path: string,
      section = 'Claims'
    ): NavItem => ({
      key,
      label,
      to: `${routes.healthClaims.index}/${path}`,
      icon: iconFor(key),
      submenus: [],
      section,
    });
    const reimbursementClaims = `${routes.healthClaims.index}/${routes.healthClaims.reimbursementClaims}`;

    // Direct Billing Claims is left out of the menu for now. Its route stays,
    // so it comes back by adding the tab here again.
    return [
      tab('health-dashboard', 'Dashboard', routes.healthClaims.dashboard, ''),
      tab('closed-tickets', 'Closed Tickets', routes.healthClaims.closedTickets),
      tab(
        'reimbursement-approval',
        'Approvals',
        routes.healthClaims.reimbursementApproval
      ),
      {
        key: 'reimbursement-claims',
        label: 'Reimbursement Claims',
        icon: iconFor('reimbursement-claims'),
        section: 'Claims',
        submenus: [
          {
            key: 'reimbursement-claims-azadea',
            label: 'AZADEA',
            to: `${reimbursementClaims}/${routes.healthClaims.reimbursementAzadea}`,
          },
          {
            key: 'reimbursement-claims-global',
            label: 'GLOBAL',
            to: `${reimbursementClaims}/${routes.healthClaims.reimbursementGlobal}`,
          },
        ],
      },
    ];
  }

  return groupBySection([
    {
      key: 'dashboard',
      label: labelFor('dashboard', 'Dashboard'),
      to: routes.main,
      icon: iconFor('dashboard'),
      submenus: [],
    },
    {
      key: 'list0',
      label: labelFor('list0', 'List 0'),
      icon: iconFor('list0'),
      submenus: [
        {
          key: 'list0-submit',
          label: 'Submit & History',
          to: routes.listZero.index,
        },
        {
          key: 'list0-participants',
          label: 'Participants',
          to: `${routes.listZero.index}/${routes.listZero.participants}`,
        },
      ],
    },
    ...serverMenu.map((item) => ({
      key: item.identifier,
      label: labelFor(item.identifier, item.name),
      to: item.submenus.length > 0 ? undefined : routeFor(item.identifier),
      icon: iconFor(item.identifier),
      submenus: item.submenus.map((submenu) => ({
        key: `${item.identifier}-${submenu.identifier}`,
        label: submenu.name,
        to: submenuRouteFor(submenu.identifier),
      })),
    })),
  ]);
}

/*
 * The sidebar while you are in the documentation.
 *
 * One group per product, so cargo and health claims have somewhere to put their
 * documentation when it is written. Replaces the in-page tab strip the docs
 * used to carry, which was a third navigation pattern on top of products across
 * the top and sections down the side.
 */
export function buildDocsNavItems(): NavItem[] {
  return DOCS_SECTIONS.map((section) => ({
    key: `docs-${section.key}`,
    label: section.label,
    icon: section.icon,
    submenus: section.pages.map((page) => ({
      key: page.key,
      label: page.label,
      to: page.path,
    })),
  }));
}
